"use strict";
/* eslint-disable @typescript-eslint/no-explicit-any */
//justification: exposing the same interface as the underlying logger
Object.defineProperty(exports, "__esModule", { value: true });
exports.AzureFunctionLogger = void 0;
/**
 * Logger for azure functions that utilizes an instance of AppInsightsClient to log to Application Insights.  If no
 * AppInsightsClient is given, this can use the built in Context.log object supplied to the functions.  Constructor params are
 * provided for both.
 * Utilizes @azure/functions package
 */
class AzureFunctionLogger {
    constructor(functionLogger, appInsightsClient) {
        this.functionLogger = functionLogger;
        this.appInsightsClient = appInsightsClient;
        this.properties = {};
    }
    /**
     * Calls @function setLogProperty for each key/value pair on properties. (Does *not* remove any properties with other keys).
     * @param properties The properties to set on the logger.
     */
    setLogProperties(properties) {
        Object.entries(properties).forEach(([key, value]) => {
            this.setLogProperty(key, value);
        });
    }
    /**
     * Sets properties that are appended to every log in app insights. Not used if no app insights client given to this logger.
     *
     * @param key The key/name of the property
     * @param value Its value
     */
    setLogProperty(key, value) {
        this.properties[key] = value;
    }
    log(message, optionalParams, severity) {
        const properties = Object.entries(this.properties);
        const commaSeparatedProperties = properties.map(([k, v]) => `${k}: ${v}`).join(", ");
        const prefixedMessage = properties.length > 0 ? `(${commaSeparatedProperties}) ${message}` : message;
        switch (severity) {
            case "VERBOSE":
                this.functionLogger.verbose(prefixedMessage, ...optionalParams);
                break;
            case "CRITICAL":
                //Intentional, there's no context.log.critical
                this.functionLogger.error(prefixedMessage, ...optionalParams);
                break;
            case "ERROR":
                this.functionLogger.error(prefixedMessage, ...optionalParams);
                break;
            case "INFORMATION":
                this.functionLogger.info(prefixedMessage, ...optionalParams);
                break;
            case "WARNING":
                this.functionLogger.warn(prefixedMessage, ...optionalParams);
                break;
            default:
                //Verbose is used as the default
                this.functionLogger.verbose(prefixedMessage, ...optionalParams);
                break;
        }
    }
    /**
     * Logs a message at debug level
     * @param message The log message
     * @param optionalParams Any optional params; will be passed through to the underlying Azure Logger.logLevel() call, so behaviour will be the same as standard logging in the @azure/functions package
     */
    debug(message, ...optionalParams) {
        this.log(message || "", optionalParams, "VERBOSE");
    }
    /**
     * Logs a message at info level
     * @param message The log message
     * @param optionalParams Any optional params; will be passed through to the underlying Azure Logger.logLevel() call, so behaviour will be the same as standard logging in the @azure/functions package
     */
    info(message, ...optionalParams) {
        this.log(message || "", optionalParams, "INFORMATION");
    }
    /**
     * Logs a message at warning level
     * @param message The log message
     * @param optionalParams Any optional params; will be passed through to the underlying Azure Logger.logLevel() call, so behaviour will be the same as standard logging in the @azure/functions package
     */
    warn(message, ...optionalParams) {
        this.log(message || "", optionalParams, "WARNING");
    }
    /**
     * Logs a message at error level
     * @param message The log message
     * @param optionalParams Any optional params; will be passed through to the underlying Azure Logger.logLevel() call, so behaviour will be the same as standard logging in the @azure/functions package
     */
    error(message, ...optionalParams) {
        this.log(message || "", optionalParams, "ERROR");
    }
    /**
     * Logs a message at error level (@azure/functions Logger does not have a critical log level)
     * @param message The log message
     * @param optionalParams Any optional params; will be passed through to the underlying Azure Logger.logLevel() call, so behaviour will be the same as standard logging in the @azure/functions package
     */
    critical(message, ...optionalParams) {
        this.log(message || "", optionalParams, "CRITICAL");
    }
}
exports.AzureFunctionLogger = AzureFunctionLogger;
